// Every request the board makes, and the three ways one can fail that the page treats differently.

import type { AuthConfig, LeadershipNextStep, PipelinePayload, TouchHistory, Viewer } from './types.ts';

export type TokenGetter = () => Promise<string | null>;

// The session is gone; the sign-in gate is the answer, not an error banner.
export class SessionExpiredError extends Error {
  name = 'SessionExpiredError';
}

// Signed in and verified, but not in this app's roles.json. Signing in again changes nothing.
export class AccessDeniedError extends Error {
  name = 'AccessDeniedError';
}

// The back end is still running its first build after a start (payload.md: `503 building`).
// Not a failure: the page waits and asks again after `retry_after_seconds`.
export class StillBuildingError extends Error {
  name = 'StillBuildingError';
  retry_after_seconds: number;

  constructor(message: string, retryAfterSeconds: number) {
    super(message);
    this.retry_after_seconds = retryAfterSeconds;
  }
}

// FastAPI refusals carry `detail`, either a string or `{code, message}` (payload.md), and the
// 503 carries `{code, message}` at the top level. One reader for all of them.
interface FailureBody {
  detail?: string | { code?: string; message?: string };
  code?: string;
  message?: string;
}

function failureMessage(body: FailureBody): string | null {
  if (typeof body.detail === 'string') return body.detail;
  return body.detail?.message ?? body.message ?? null;
}

function failureCode(body: FailureBody): string | null {
  if (typeof body.detail === 'object' && body.detail?.code) return body.detail.code;
  return body.code ?? null;
}

export class ApiError extends Error {
  name = 'ApiError';
  status: number;
  // payload.md's `detail.code` (or top-level `code`), when the body carried one.
  code: string | null;

  constructor(message: string, status: number, code: string | null = null) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

// A GET, or a POST of `body` as JSON when one is given (every mutation is a POST).
async function request<T>(path: string, getToken: TokenGetter, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  const token = await getToken();
  if (token) headers.Authorization = 'Bearer ' + token;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const init: RequestInit = body === undefined ? { headers } : { method: 'POST', headers, body: JSON.stringify(body) };
  const response = await fetch(path, init);
  if (response.status === 401) throw new SessionExpiredError('Your session has expired.');

  // A body that won't parse still has to read as a refusal.
  if (!response.ok) {
    const failure = (await response.json().catch(() => ({}))) as FailureBody;
    const message = failureMessage(failure);
    if (response.status === 403) {
      throw new AccessDeniedError(message || 'Ask Vikram to be given access.');
    }
    if (response.status === 503 && failure.code === 'building') {
      const retryAfterSeconds = Number(response.headers.get('Retry-After')) || 5;
      throw new StillBuildingError(message || 'The pipeline is still being built.', retryAfterSeconds);
    }
    throw new ApiError(message || response.statusText, response.status, failureCode(failure));
  }
  return (await response.json()) as T;
}

// Open, so an anonymous visitor can mount the sign-in form at all.
export async function fetchAuthConfig(): Promise<AuthConfig> {
  const response = await fetch('/api/auth/config');
  if (!response.ok) throw new Error('The server did not return its sign-in configuration.');
  return (await response.json()) as AuthConfig;
}

export function fetchPipeline(getToken: TokenGetter): Promise<PipelinePayload> {
  return request<PipelinePayload>('/api/pipeline', getToken);
}

// The drawer's one request, made when it opens.
export function fetchHistory(getToken: TokenGetter, oppId: string): Promise<TouchHistory> {
  return request<TouchHistory>(`/api/pipeline/${encodeURIComponent(oppId)}/history`, getToken);
}

// Who is looking, so the drawer knows whether to offer the leadership next step's editor (GRO-238).
export function fetchViewer(getToken: TokenGetter): Promise<Viewer> {
  return request<Viewer>('/api/me', getToken);
}

// Save (or, with an empty text, clear) a leadership next step. Written to Salesforce before it answers.
export function saveLeadershipNextStep(
  getToken: TokenGetter, oppId: string, text: string,
): Promise<{ opp_id: string; leadership_next_step: LeadershipNextStep | null }> {
  return request(`/api/pipeline/${encodeURIComponent(oppId)}/leadership-next-step`, getToken, { text });
}
